export const QUANT_WEAK_THRESHOLD = 155;

export const QUANT_TRACK_STORAGE_KEY = 'gre_quant_track';
const QUANT_DIAGNOSTIC_SCORE_STORAGE_KEY = 'gre_quant_diagnostic_score';

export const QUANT_TRACKS = {
  PENDING_DIAGNOSTIC: 'pending_diagnostic',
  FOUNDATIONS: 'foundations',
  ACCELERATION: 'acceleration',
};

const parseScore = (value) => {
  const score = Number(value);
  return Number.isFinite(score) && score > 0 ? score : null;
};

const normalizeEmailSegment = (email) =>
  encodeURIComponent(String(email || '').trim().toLowerCase());

const getCurrentUserEmail = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    return user?.email || localStorage.getItem('userEmail') || null;
  } catch {
    return localStorage.getItem('userEmail') || null;
  }
};

const getUserScopedKey = (baseKey, userEmail = getCurrentUserEmail()) =>
  userEmail ? `${baseKey}_${normalizeEmailSegment(userEmail)}` : null;

export const getQuantTrackFromScore = (quantScore) => {
  const score = parseScore(quantScore);
  if (score === null) return QUANT_TRACKS.PENDING_DIAGNOSTIC;
  return score < QUANT_WEAK_THRESHOLD
    ? QUANT_TRACKS.FOUNDATIONS
    : QUANT_TRACKS.ACCELERATION;
};

export const syncQuantTrackFromDiagnostic = (quantScore) => {
  const track = getQuantTrackFromScore(quantScore);
  const trackKey = getUserScopedKey(QUANT_TRACK_STORAGE_KEY);
  const scoreKey = getUserScopedKey(QUANT_DIAGNOSTIC_SCORE_STORAGE_KEY);
  if (!trackKey || !scoreKey) return track;

  try {
    localStorage.setItem(trackKey, track);
    localStorage.setItem(scoreKey, String(quantScore || ''));
  } catch {
    // localStorage can be unavailable in private browsing or tests.
  }
  return track;
};

export const setQuantTrackChoice = (track) => {
  if (
    track !== QUANT_TRACKS.FOUNDATIONS &&
    track !== QUANT_TRACKS.ACCELERATION
  ) {
    return getQuantTrack();
  }

  const trackKey = getUserScopedKey(QUANT_TRACK_STORAGE_KEY);
  if (!trackKey) return track;

  try {
    localStorage.setItem(trackKey, track);
  } catch {
    // localStorage can be unavailable in private browsing or tests.
  }
  return track;
};

export const getStoredFullTestOneQuantScore = () => {
  try {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) return null;
    const detailedResults = JSON.parse(
      localStorage.getItem(`gre_detailed_results_${normalizeEmailSegment(userEmail)}_full-test-1`) || 'null'
    );
    return parseScore(detailedResults?.enhancedScores?.quant?.enhanced);
  } catch {
    return null;
  }
};

export const getRecommendedQuantTrack = () =>
  getQuantTrackFromScore(getStoredFullTestOneQuantScore());

export const getQuantTrack = () => {
  const trackKey = getUserScopedKey(QUANT_TRACK_STORAGE_KEY);
  try {
    const storedTrack = trackKey ? localStorage.getItem(trackKey) : null;
    if (
      storedTrack === QUANT_TRACKS.FOUNDATIONS ||
      storedTrack === QUANT_TRACKS.ACCELERATION
    ) {
      return storedTrack;
    }
  } catch {
    // Ignore storage errors and fall back to the diagnostic recommendation.
  }

  const score = getStoredFullTestOneQuantScore();
  if (score !== null) return syncQuantTrackFromDiagnostic(score);

  return QUANT_TRACKS.PENDING_DIAGNOSTIC;
};

export const isWeakQuantCandidate = () =>
  getQuantTrack() === QUANT_TRACKS.FOUNDATIONS;

export const getDefaultPracticeDifficulty = () =>
  isWeakQuantCandidate() || getQuantTrack() === QUANT_TRACKS.PENDING_DIAGNOSTIC
    ? 'medium'
    : 'hard';

export const getVeryHardUnlockThreshold = () =>
  isWeakQuantCandidate() ? 75 : 70;

export const getQuantTrackLabel = (track = getQuantTrack()) => {
  if (track === QUANT_TRACKS.FOUNDATIONS) return 'Foundations';
  if (track === QUANT_TRACKS.ACCELERATION) return 'Acceleration';
  return 'Diagnostic pending';
};
